import React, { useEffect, useMemo, useRef, useState } from "react";
import mammoth from "mammoth";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import WebViewer from "@pdftron/webviewer";
import styles from "./DocumentPreviewModal.module.css";
import downloadIcon from "../../../assets/images/download.svg";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getFileExtension = (fileName) => {
  if (!fileName) return "";
  return fileName.split(".").pop().toLowerCase();
};

const getExtensionFromUrl = (value) => {
  if (!value) return "";
  const cleanUrl = String(value).split("?")[0].split("#")[0];
  const lastSegment = cleanUrl.split("/").pop() || "";
  return getFileExtension(lastSegment);
};

const WEBVIEWER_PRESENTATION_EXTENSIONS = new Set([
  "ppt",
  "pptx",
  "ppx",
  "pptm",
  "pps",
  "ppsx",
  "pot",
  "potx",
  "potm",
]);
const CAD_EXTENSIONS = new Set(["dwg", "dxf", "dwf", "dgn", "rvt"]);
export default function DocumentPreviewModal({ file, onClose, isOpen }) {
  const viewerDivRef = useRef(null);
  const instanceRef = useRef(null);
  const pdfScrollContainerRef = useRef(null);
  const pdfPageRefs = useRef([]);
  const [viewerError, setViewerError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [wordHtml, setWordHtml] = useState("");
  const [textContent, setTextContent] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState(null);

  const fileName =
    file?.name || file?.fileName || file?.documentName || file?.title || "";

  const extension = getFileExtension(fileName) || getExtensionFromUrl(file?.url);
  const src = file?.blobUrl || file?.url || "";
  const contentType = String(file?.contentType || "").toLowerCase();

  const isVideo =
    contentType.startsWith("video/") || ["mp4", "webm", "ogg"].includes(extension);
  const isPdf = contentType === "application/pdf" || extension === "pdf";
  const isDocxMime = contentType.includes("wordprocessingml.document");
  const isDocx = extension === "docx" || isDocxMime;
  const isWordDoc = isDocx;
  const isTextFile =
    contentType.startsWith("text/plain") ||
    contentType.startsWith("text/csv") ||
    extension === "txt" ||
    extension === "csv";
  const isCadFile = CAD_EXTENSIONS.has(extension);
  const canOpenInWebViewer =
    Boolean(src) && WEBVIEWER_PRESENTATION_EXTENSIONS.has(extension);

  const pdfThumbWidth = 150;
  const pdfMainWidth = 900;

  const pageThumbs = useMemo(
    () => (numPages ? Array.from({ length: numPages }, (_, index) => index + 1) : []),
    [numPages],
  );

  const scrollToPdfPage = (targetPage) => {
    const nextPage = Math.max(1, Math.min(numPages || targetPage, targetPage));
    const container = pdfScrollContainerRef.current;
    const targetNode = pdfPageRefs.current[nextPage - 1];

    setPageNumber(nextPage);

    if (!container || !targetNode) return;

    const nextTop = Math.max(0, targetNode.offsetTop - 20);
    if (typeof container.scrollTo === "function") {
      container.scrollTo({
        top: nextTop,
        behavior: "smooth",
      });
    } else {
      container.scrollTop = nextTop;
    }
  };

  useEffect(() => {
    if (!isOpen || !file) {
      setWordHtml("");
      setTextContent("");
      setConversionError(null);
      setIsConverting(false);
      return;
    }

    const convertDocument = async () => {
      setIsConverting(true);
      setConversionError(null);
      setWordHtml("");
      setTextContent("");
      try {
        if (isDocx) {
          const response = await fetch(src);
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setWordHtml(result.value);
        } else if (isTextFile) {
          const response = await fetch(src);
          const text = await response.text();
          setTextContent(text);
        }
      } catch (error) {
        console.error("Failed to convert document:", error);
        setConversionError(`Failed to preview document: ${error.message}`);
      } finally {
        setIsConverting(false);
      }
    };

    if (isDocx || isTextFile) {
      convertDocument();
    }
  }, [isOpen, file, isDocx, isTextFile, src]);

  useEffect(() => {
    let isDisposed = false;

    const initWebViewer = async () => {
      if (!isOpen || !file || !viewerDivRef.current || !canOpenInWebViewer) {
        return;
      }

      setViewerError("");

      try {
        const instance = await WebViewer(
          {
            path: "/webviewer/lib",
            licenseKey: import.meta.env.VITE_APRYSE_LICENSE_KEY || "",
            disabledElements: ["downloadButton"],
          },
          viewerDivRef.current,
        );

        if (isDisposed) {
          instance.UI.dispose();
          return;
        }

        instanceRef.current = instance;
        await instance.UI.loadDocument(src, {
          filename: fileName || undefined,
        });
      } catch (error) {
        if (!isDisposed) {
          setViewerError(error?.message || "Failed to preview this file.");
        }
      }
    };

    initWebViewer();

    return () => {
      isDisposed = true;
      if (instanceRef.current) {
        instanceRef.current.UI.dispose();
        instanceRef.current = null;
      }
    };
  }, [isOpen, file, src, fileName, canOpenInWebViewer]);

  useEffect(() => {
    if (!isPdf || !numPages || !pdfScrollContainerRef.current) return;

    const container = pdfScrollContainerRef.current;

    const syncVisiblePage = () => {
      const containerRect = container.getBoundingClientRect();
      const anchor = containerRect.top + 24;
      let nextPage = 1;
      let nearestDistance = Number.POSITIVE_INFINITY;

      pdfPageRefs.current.forEach((node, index) => {
        if (!node) return;
        const distance = Math.abs(node.getBoundingClientRect().top - anchor);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nextPage = index + 1;
        }
      });

      setPageNumber((prev) => (prev === nextPage ? prev : nextPage));
    };

    syncVisiblePage();
    container.addEventListener("scroll", syncVisiblePage, { passive: true });
    window.addEventListener("resize", syncVisiblePage);

    return () => {
      container.removeEventListener("scroll", syncVisiblePage);
      window.removeEventListener("resize", syncVisiblePage);
    };
  }, [isPdf, numPages]);

  if (!isOpen || !file) {
    return null;
  }

  const onPdfLoadSuccess = ({ numPages: loadedPages }) => {
    pdfPageRefs.current = [];
    setNumPages(loadedPages);
    setPageNumber(1);
    if (pdfScrollContainerRef.current) {
      if (typeof pdfScrollContainerRef.current.scrollTo === "function") {
        pdfScrollContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
      } else {
        pdfScrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  const handleSaveAs = async () => {
    const downloadUrl = file.blobUrl || file.url || "";
    if (!downloadUrl) return;

    const downloadName =
      file.name || file.fileName || file.documentName || file.title || "download";

    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      if (window.showSaveFilePicker) {
        const ext = downloadName.includes(".")
          ? downloadName.split(".").pop().toLowerCase()
          : "";
        const mimeType = blob.type || "application/octet-stream";
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: downloadName,
          types: ext
            ? [{ description: "File", accept: { [mimeType]: ["." + ext] } }]
            : [{ description: "All Files", accept: { "application/octet-stream": [] } }],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Save failed:", err);
      }
    }
  };

  return (
    <div className={styles["modal-overlay"]} onClick={onClose}>
      <div className={styles["modal-content"]} onClick={(e) => e.stopPropagation()}>
        <div className={styles["modal-header"]}>
          {isPdf ? (
            <div className={styles.pdfHeaderControls}>
              <button
                type="button"
                className={styles.headerPrevButton}
                onClick={() => scrollToPdfPage(pageNumber - 1)}
                disabled={pageNumber <= 1}
              >
                Previous
              </button>
              <span className={styles.headerPageIndicator}>
                Page {pageNumber} of {numPages || "--"}
              </span>
              <button
                type="button"
                className={styles.headerNextButton}
                onClick={() => scrollToPdfPage(pageNumber + 1)}
                disabled={!numPages || pageNumber >= numPages}
              >
                Next
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className={styles["header-download-button"]}
            onClick={handleSaveAs}
            title={`Save ${fileName}`}
          >
            <img src={downloadIcon} alt="" aria-hidden="true" />
            <span>Save</span>
          </button>
          <button className={styles["modal-close-button"]} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles["modal-body"]}>
          {!src ? (
            <div className={styles.previewFallback}>
              <p>Preview not available for this file.</p>
              <button
                type="button"
                className={styles.previewDownloadBtn}
                onClick={handleSaveAs}
              >
                Save
              </button>
            </div>
          ) : isVideo ? (
            <video src={src} controls width="100%" height="auto" />
          ) : canOpenInWebViewer ? (
            viewerError ? (
              <div className={styles.previewFallback}>
                <p>{viewerError}</p>
                <button
                  type="button"
                  className={styles.previewDownloadBtn}
                  onClick={handleSaveAs}
                >
                  Save
                </button>
              </div>
            ) : (
              <div ref={viewerDivRef} className={styles.webviewerContainer} />
            )
          ) : isPdf ? (
            <div className={styles.pdfPreviewContainer}>
              {numPages ? (
                <div className={styles.pdfSidebar}>
                  <Document file={src}>
                    {pageThumbs.map((thumbPageNumber) => (
                      <button
                        key={`thumb_${thumbPageNumber}`}
                        type="button"
                        className={`${styles.pdfThumbnail} ${
                          pageNumber === thumbPageNumber ? styles.pdfThumbnailActive : ""
                        }`}
                        onClick={() => scrollToPdfPage(thumbPageNumber)}
                      >
                        <Page
                          pageNumber={thumbPageNumber}
                          width={pdfThumbWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                        <div className={styles.pdfThumbnailLabel}>{thumbPageNumber}</div>
                      </button>
                    ))}
                  </Document>
                </div>
              ) : null}

              <div className={styles.pdfMain}>
                <div ref={pdfScrollContainerRef} className={styles.pdfDocumentContainer}>
                  <Document
                    file={src}
                    onLoadSuccess={onPdfLoadSuccess}
                    loading={<div className={styles.previewLoading}>Loading PDF...</div>}
                    error={<div className={styles.previewFallback}>Failed to load PDF.</div>}
                  >
                    <div className={styles.pdfPagesStack}>
                      {pageThumbs.map((pdfPageNumber) => (
                        <div
                          key={`page_${pdfPageNumber}`}
                          ref={(node) => {
                            pdfPageRefs.current[pdfPageNumber - 1] = node;
                          }}
                          className={styles.pdfPageWrap}
                        >
                          <Page
                            pageNumber={pdfPageNumber}
                            width={pdfMainWidth}
                            renderTextLayer
                            renderAnnotationLayer
                            className={styles.pdfPage}
                          />
                        </div>
                      ))}
                    </div>
                  </Document>
                </div>
              </div>
            </div>
          ) : isWordDoc ? (
            <div className={styles.wordPreview}>
              {isConverting ? (
                <div className={styles.previewLoading}>Converting document...</div>
              ) : conversionError ? (
                <div className={styles.previewFallback}>
                  <p>{conversionError}</p>
                  <button
                    type="button"
                    className={styles.previewDownloadBtn}
                    onClick={handleSaveAs}
                  >
                    Save
                  </button>
                </div>
              ) : (
                <div
                  className={styles.wordContent}
                  dangerouslySetInnerHTML={{ __html: wordHtml }}
                />
              )}
            </div>
          ) : isTextFile ? (
            <div className={styles.textPreview}>
              {isConverting ? (
                <div className={styles.previewLoading}>Loading text file...</div>
              ) : conversionError ? (
                <div className={styles.previewFallback}>
                  <p>{conversionError}</p>
                  <button
                    type="button"
                    className={styles.previewDownloadBtn}
                    onClick={handleSaveAs}
                  >
                    Save
                  </button>
                </div>
              ) : (
                <pre className={styles.textContent}>{textContent}</pre>
              )}
            </div>
          ) : (
            <div className={styles.previewFallback}>
              {isCadFile ? (
                <>
                  <p>
                    <strong>.{extension.toUpperCase()} (CAD) files cannot be previewed in the browser.</strong>
                  </p>
                  <p style={{ fontSize: "0.875rem", color: "#555" }}>
                    You can download the file to open it in a CAD application.
                  </p>
                </>
              ) : (
                <p>Preview is not available for this file type.</p>
              )}
              <button
                type="button"
                className={styles.previewDownloadBtn}
                onClick={handleSaveAs}
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

